import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import IconAddCircle from '../../../assets/images/Icons/IconAddCircle';
import IcHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import isMinus from '../../../assets/images/svg/ic-minus-circle.svg';
import isSun from '../../../assets/images/svg/ic-sun.svg';
import { setNodeData } from '../../../features/mainSlice';
import Checkbox from '../../shared/Checkbox';
import TextField from '../../shared/TextField';
import Tooltip from '../../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      marginTop: 16,
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
      '& > span': {
        color: color.$17,
      },
      '& > img': {
        marginLeft: 4,
      },
    },
    sun: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$7,
      background: color.$15,
      width: 80,
      borderRadius: '20px',
      justifyContent: 'center',
      padding: '2px 0',
      marginBottom: 6,
    },
    hint: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 24,
    },
    input: {
      marginBottom: 12,
    },
    stickerLabel: {
      fontSize: '15px',
      fontWeight: "'400'",
      lineHeight: 1,
      color: color.$13,
    },
    stickerBlock: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
      '& > img': {
        marginLeft: 8,
      },
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      marginBottom: 12,
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    packageIdTooltip: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$12,
      '& > div': {
        fontWeight: '600',
      },
    },
  },
  {
    name: 'Sticker',
  },
);

const Sticker = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode, isErrorNodeIds } = useSelector(state => state.ma);

  const groupId = selectedNode?.id;
  const { stickerIds, packageId } = selectedNode?.data;

  const isError = !!~isErrorNodeIds.indexOf(groupId);

  const onPackageIdChange = value => {
    dispatch(
      setNodeData({
        packageId: value.trim(),
      }),
    );
  };

  const onStickerCheckboxChange = value => {
    dispatch(
      setNodeData({
        stickerIds: value ? [''] : [],
      }),
    );
  };

  const onStickerIdChange = (value, index) => {
    dispatch(
      setNodeData({
        stickerIds: stickerIds.map((s, i) => {
          if (i === index) {
            return value.trim();
          }
          return s;
        }),
      }),
    );
  };

  const onAddStickerId = () => {
    dispatch(
      setNodeData({
        stickerIds: [...stickerIds, ''],
      }),
    );
  };

  const onDeleteCondition = index => {
    dispatch(
      setNodeData({
        stickerIds: stickerIds.filter((s, i) => i !== index),
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {t('condition-setting.packageId')}
        <span>*</span>
        <Tooltip
          light
          placement="top"
          isComponent
          title={
            <div className={classes.packageIdTooltip}>
              <div>{t('condition-setting.packageIdTooltipTitle1')}</div>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('condition-setting.packageIdTooltipContent1'),
                }}
              />
              <div>{t('condition-setting.packageIdTooltipTitle2')}</div>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('condition-setting.packageIdTooltipContent2'),
                }}
              />
            </div>
          }
        >
          <img className={classes.marginLeft8} src={IcHelpCircle} alt="alert" />
        </Tooltip>
      </div>
      <div style={{ marginBottom: 12 }}>
        <TextField
          className={classes.input}
          type="text"
          isValid
          placeholder={t('condition-setting.packageIdPlaceholder')}
          value={packageId}
          onChange={event => onPackageIdChange(event?.target?.value)}
          isError={isError && !packageId}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!stickerIds?.length}
              onChange={e => {
                onStickerCheckboxChange(e.target.checked);
              }}
              name="stickerIds"
              color="primary"
            />
          }
          label={
            <div className={classes.stickerLabel}>
              {t('condition-setting.stickerIdCheckBox')}
            </div>
          }
        />
      </div>
      {!!stickerIds?.length && (
        <div>
          <div className={classes.title} style={{ marginTop: 24 }}>
            {t('condition-setting.stickerId')}
            <span>*</span>
          </div>
          {stickerIds.map((stickerId, index) => (
            <div key={`stickerId-${index}`} className={classes.stickerBlock}>
              <TextField
                type="text"
                isValid
                placeholder={t('condition-setting.stickerIdPlaceholder')}
                value={stickerId}
                onChange={event =>
                  onStickerIdChange(event?.target?.value, index)
                }
                isError={isError && !stickerId}
              />
              {stickerIds.length > 1 && (
                <img
                  className={classes.minus}
                  onClick={() => onDeleteCondition(index)}
                  src={isMinus}
                  alt="minus"
                />
              )}
            </div>
          ))}
          <div className={classes.icon} onClick={onAddStickerId}>
            <IconAddCircle id="icon-add-message-type" width={20} height={20} />
            {t('condition-setting.stickerIdAdd')}
          </div>
        </div>
      )}
      <div className={classes.sun}>
        <img src={isSun} alt="minus" />
        {t('trigger-setting.sun')}
      </div>
      <div className={classes.hint}>{t('condition-setting.stickerIdHint')}</div>
    </div>
  );
};

export default Sticker;
